import { useState } from "react";
import {
	Plus,
	Pencil,
	Trash2,
	Lock,
	Check,
	GripVertical,
	Loader2,
	FolderGit2,
	ArrowUpDown,
} from "lucide-react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useApiSWR } from "@/hooks/useApiSWR";
import { usePermission } from "@/hooks/usePermission";
import { PermissionGate } from "@/components/PermissionGate";
import { COLOR_PRESETS, StageChip } from "@/components/tasks/stage-ui";
import { SYSTEM_STAGES } from "@/components/tasks/system-stages";
import { listProjects, type Project } from "@/services/project.service";
import {
	listCatalogStages,
	createCatalogStage,
	updateCatalogStage,
	deleteCatalogStage,
	getStagePlacement,
	setStagePlacement,
	setCatalogStageProjects,
	type WorkflowStageCatalog,
	type WorkflowStagePlacementEntry,
} from "@/services/workflow-stage-catalog.service";
import { SectionBlock } from "./SectionBlock";

const MANAGE = "Manage role permissions";
const DEFAULT_STAGES = SYSTEM_STAGES.filter((s) => s.key !== "cancelled");

function arraysEqualAsSets(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	const setB = new Set(b);
	return a.every((id) => setB.has(id));
}

function ProjectsCell({
	stage,
	projects,
	canManage,
	onChanged,
}: {
	stage: WorkflowStageCatalog;
	projects: Project[];
	canManage: boolean;
	onChanged: () => void;
}) {
	const [saving, setSaving] = useState(false);
	// Seeded once from the server value; intentionally not re-synced on every
	// prop change so picks made while the popover is closed are preserved
	// until the user explicitly saves or cancels.
	const [draft, setDraft] = useState<string[]>(stage.projectIds);
	const dirty = !arraysEqualAsSets(draft, stage.projectIds);
	const draftSet = new Set(draft);

	function toggle(projectId: string, checked: boolean) {
		setDraft((prev) =>
			checked ? [...prev, projectId] : prev.filter((id) => id !== projectId),
		);
	}

	function handleCancel() {
		setDraft(stage.projectIds);
	}

	async function handleSave() {
		setSaving(true);
		try {
			await setCatalogStageProjects(stage.id, draft);
			toast.success("Project applicability saved");
			onChanged();
		} catch (err) {
			toast.error((err as Error).message || "Failed to update projects.");
		} finally {
			setSaving(false);
		}
	}

	const label =
		stage.projectIds.length === 0
			? "No projects"
			: `${stage.projectIds.length} project${stage.projectIds.length !== 1 ? "s" : ""}`;

	if (!canManage) {
		return <span className="text-xs text-muted">{label}</span>;
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="gap-1.5">
					<FolderGit2 className="h-3.5 w-3.5" />
					{label}
					{dirty && (
						<span
							className="h-1.5 w-1.5 rounded-full bg-primary"
							aria-label="Unsaved changes"
						/>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-64 p-2">
				<p className="px-2 py-1.5 text-xs font-medium text-muted">
					Apply to projects
				</p>
				<div className="max-h-64 overflow-y-auto">
					{projects.length === 0 ? (
						<p className="px-2 py-3 text-xs text-muted">
							No projects available.
						</p>
					) : (
						projects.map((p) => (
							<label
								key={p.id}
								className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted-subtle"
							>
								<Checkbox
									checked={draftSet.has(p.id)}
									disabled={saving}
									onCheckedChange={(v) =>
										toggle(p.id, v === true)
									}
								/>
								<span className="truncate">{p.name}</span>
							</label>
						))
					)}
				</div>
				<div className="mt-2 flex items-center justify-end gap-2 border-t border-border pt-2">
					<Button
						variant="outline"
						size="sm"
						onClick={handleCancel}
						disabled={saving || !dirty}
					>
						Cancel
					</Button>
					<Button
						size="sm"
						onClick={handleSave}
						disabled={saving || !dirty}
					>
						{saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
						Save
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}

function CustomStageRow({
	stage,
	index,
	projects,
	canManage,
	onEdit,
	onDelete,
	onChanged,
}: {
	stage: WorkflowStageCatalog;
	index: number;
	projects: Project[];
	canManage: boolean;
	onEdit: (stage: WorkflowStageCatalog) => void;
	onDelete: (stage: WorkflowStageCatalog) => void;
	onChanged: () => void;
}) {
	return (
		<tr className="border-b border-border last:border-0 hover:bg-muted-subtle transition-colors">
			<td className="px-4 py-3 text-sm text-muted w-10">{index + 1}</td>
			<td className="px-4 py-3">
				<StageChip label={stage.name} color={stage.color} />
			</td>
			<td className="px-4 py-3">
				<ProjectsCell
					stage={stage}
					projects={projects}
					canManage={canManage}
					onChanged={onChanged}
				/>
			</td>
			<td className="px-4 py-3 text-right">
				{canManage && (
					<div className="flex items-center justify-end gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => onEdit(stage)}
						>
							<Pencil className="h-3.5 w-3.5" />
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="text-danger border-danger/30 hover:bg-danger/5"
							onClick={() => onDelete(stage)}
						>
							<Trash2 className="h-3.5 w-3.5" />
						</Button>
					</div>
				)}
			</td>
		</tr>
	);
}

function StageDialog({
	open,
	onClose,
	editing,
	existing,
	onSaved,
}: {
	open: boolean;
	onClose: () => void;
	editing: WorkflowStageCatalog | null;
	existing: WorkflowStageCatalog[];
	onSaved: () => void;
}) {
	return (
		<Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
			{open && (
				<StageDialogBody
					key={editing?.id ?? "new"}
					editing={editing}
					existing={existing}
					onSaved={onSaved}
					onClose={onClose}
				/>
			)}
		</Dialog>
	);
}

function StageDialogBody({
	editing,
	existing,
	onSaved,
	onClose,
}: {
	editing: WorkflowStageCatalog | null;
	existing: WorkflowStageCatalog[];
	onSaved: () => void;
	onClose: () => void;
}) {
	// Mounted fresh each time the dialog opens (keyed by editing target in the
	// parent), so initial state can read straight from props with no reset effect.
	const [name, setName] = useState(editing?.name ?? "");
	const [color, setColor] = useState(editing?.color ?? COLOR_PRESETS[0]);
	const [saving, setSaving] = useState(false);

	async function handleSave() {
		const trimmed = name.trim();
		if (!trimmed) {
			toast.error("Stage name required");
			return;
		}
		const dup = existing.find(
			(s) =>
				s.name.toLowerCase() === trimmed.toLowerCase() &&
				s.id !== editing?.id,
		);
		if (dup) {
			toast.error("Stage name already exists");
			return;
		}

		setSaving(true);
		try {
			if (editing) {
				await updateCatalogStage(editing.id, { name: trimmed, color });
				toast.success("Stage updated");
			} else {
				await createCatalogStage({ name: trimmed, color });
				toast.success("Stage created");
			}
			onSaved();
			onClose();
		} catch (err) {
			toast.error((err as Error).message || "Save failed");
		} finally {
			setSaving(false);
		}
	}

	return (
		<DialogContent className="sm:max-w-md">
			<DialogHeader>
				<DialogTitle>
					{editing ? "Edit Stage" : "Add Custom Stage"}
				</DialogTitle>
				<DialogDescription>
					Custom stages become board columns on the projects you
					apply them to.
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-2">
				<div className="space-y-2">
					<label className="text-xs font-medium text-muted">
						Name
					</label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="e.g. Code Review"
						autoFocus
						disabled={saving}
					/>
				</div>
				<div className="space-y-2">
					<label className="text-xs font-medium text-muted">
						Status color
					</label>
					<div className="flex flex-wrap items-center gap-2">
						{COLOR_PRESETS.map((c) => {
							const active =
								color.toLowerCase() === c.toLowerCase();
							return (
								<button
									key={c}
									type="button"
									onClick={() => setColor(c)}
									className="relative h-7 w-7 rounded-full border border-border transition-transform hover:scale-110"
									style={{ backgroundColor: c }}
									aria-label={`Pick color ${c}`}
								>
									{active && (
										<Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
									)}
								</button>
							);
						})}
						<label
							className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-full border border-border"
							title="Custom color"
						>
							<span
								className="absolute inset-0"
								style={{
									background:
										"conic-gradient(red, yellow, lime, cyan, blue, magenta, red)",
								}}
							/>
							<input
								type="color"
								value={color}
								onChange={(e) => setColor(e.target.value)}
								className="absolute inset-0 cursor-pointer opacity-0"
							/>
						</label>
					</div>
					<div className="flex items-center gap-2 pt-1">
						<Input
							value={color}
							onChange={(e) => setColor(e.target.value)}
							placeholder="#000000"
							className="h-8 w-28 font-mono text-xs"
						/>
						<span className="text-xs text-muted">Preview:</span>
						<StageChip
							label={name.trim() || "Stage"}
							color={color}
						/>
					</div>
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={saving}>
					{saving && (
						<Loader2 className="h-4 w-4 animate-spin mr-2" />
					)}
					{editing ? "Save" : "Create"}
				</Button>
			</DialogFooter>
		</DialogContent>
	);
}

function DeleteDialog({
	stage,
	onClose,
	onDeleted,
}: {
	stage: WorkflowStageCatalog | null;
	onClose: () => void;
	onDeleted: () => void;
}) {
	const [deleting, setDeleting] = useState(false);

	async function handleDelete() {
		if (!stage) return;
		setDeleting(true);
		try {
			await deleteCatalogStage(stage.id);
			toast.success("Stage deleted");
			onDeleted();
			onClose();
		} catch (err) {
			toast.error((err as Error).message || "Delete failed");
		} finally {
			setDeleting(false);
		}
	}

	return (
		<Dialog open={stage !== null} onOpenChange={(v) => { if (!v) onClose(); }}>
			<DialogContent className="max-w-[420px]">
				<DialogHeader>
					<DialogTitle>Delete stage</DialogTitle>
					<DialogDescription>
						Remove "{stage?.name}" from all applied projects. Tasks in
						this stage move back to Backlog. This cannot be undone.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={onClose}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleting}
					>
						{deleting && (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						)}
						Delete
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function PlacementRow({ entry }: { entry: WorkflowStagePlacementEntry }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: entry.key });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : undefined,
		background: isDragging ? "var(--muted-subtle)" : undefined,
	};

	return (
		<li
			ref={setNodeRef}
			style={style}
			className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
		>
			<button
				type="button"
				className="text-muted hover:text-foreground cursor-grab active:cursor-grabbing"
				aria-label="Drag to reorder"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="h-4 w-4" />
			</button>
			<StageChip label={entry.name} color={entry.color} />
			{entry.is_system && (
				<span className="inline-flex items-center gap-1 text-[11px] text-muted">
					<Lock className="h-3 w-3" />
					Default
				</span>
			)}
		</li>
	);
}

function PlacementDialog({
	open,
	onClose,
	onPlacementChanged,
}: {
	open: boolean;
	onClose: () => void;
	onPlacementChanged: () => void;
}) {
	const {
		data: placement = [],
		isLoading,
		mutate,
	} = useApiSWR<WorkflowStagePlacementEntry[]>(
		open ? ["workflow-stage-placement"] : null,
		getStagePlacement,
	);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	async function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const fromIdx = placement.findIndex((s) => s.key === active.id);
		const toIdx = placement.findIndex((s) => s.key === over.id);
		if (fromIdx < 0 || toIdx < 0) return;

		const next = arrayMove(placement, fromIdx, toIdx).map((s, i) => ({
			...s,
			position: i,
		}));

		mutate(next, { revalidate: false });

		try {
			await setStagePlacement(
				next.map((s) => ({ key: s.key, position: s.position })),
			);
			await mutate();
			onPlacementChanged();
			toast.success("Placement saved");
		} catch (err) {
			toast.error((err as Error).message || "Reorder failed");
			await mutate();
		}
	}

	return (
		<Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Stage Placement</DialogTitle>
					<DialogDescription>
						Drag any stage, default or custom, to set the board
						column order.
					</DialogDescription>
				</DialogHeader>

				<div className="py-2">
					{isLoading ? (
						<div className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
							<Loader2 className="h-4 w-4 animate-spin" />
							Loading…
						</div>
					) : (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={placement.map((s) => s.key)}
								strategy={verticalListSortingStrategy}
							>
								<ul className="space-y-1.5">
									{placement.map((s) => (
										<PlacementRow key={s.key} entry={s} />
									))}
								</ul>
							</SortableContext>
						</DndContext>
					)}
				</div>

				<DialogFooter>
					<Button onClick={onClose}>Done</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function WorkflowStagesSection() {
	const { can } = usePermission();
	const canManage = can(MANAGE);

	const {
		data: stages = [],
		isLoading,
		mutate,
	} = useApiSWR<WorkflowStageCatalog[]>(
		["workflow-stage-catalog"],
		listCatalogStages,
	);
	const { data: projects = [] } = useApiSWR<Project[]>(
		["projects-for-stage-catalog"],
		() => listProjects(),
	);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<WorkflowStageCatalog | null>(null);
	const [deleteTarget, setDeleteTarget] =
		useState<WorkflowStageCatalog | null>(null);
	const [placementOpen, setPlacementOpen] = useState(false);

	function openCreate() {
		setEditing(null);
		setDialogOpen(true);
	}

	function openEdit(stage: WorkflowStageCatalog) {
		setEditing(stage);
		setDialogOpen(true);
	}

	return (
		<>
			<SectionBlock
				title="Default Stages"
				description="These five stages apply to every project and cannot be removed."
			>
				<div className="flex flex-wrap gap-2">
					{DEFAULT_STAGES.map((s) => (
						<span
							key={s.key}
							className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5"
						>
							<StageChip label={s.name} color={s.color} />
							<span className="inline-flex items-center gap-1 text-[11px] text-muted">
								<Lock className="h-3 w-3" />
								Default
							</span>
						</span>
					))}
				</div>
			</SectionBlock>

			<SectionBlock
				title="Custom Stages"
				description="Add workspace stages and choose which projects they apply to."
			>
				<div className="flex items-center justify-between mb-4">
					<p className="text-sm text-muted">
						{isLoading
							? "Loading…"
							: `${stages.length} custom stage${stages.length !== 1 ? "s" : ""}`}
					</p>
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							className="gap-2"
							onClick={() => setPlacementOpen(true)}
						>
							<ArrowUpDown className="h-3.5 w-3.5" />
							Placement
						</Button>
						<PermissionGate feature={MANAGE}>
							<Button size="sm" className="gap-2" onClick={openCreate}>
								<Plus className="h-3.5 w-3.5" />
								Add Stage
							</Button>
						</PermissionGate>
					</div>
				</div>

				<div className="rounded-lg border border-border overflow-hidden">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border bg-muted-subtle">
								{["#", "Stage", "Projects", "Action"].map(
									(h, i) => (
										<th
											key={h}
											className={
												"px-4 py-2.5 text-xs font-medium text-muted " +
												(i === 3
													? "text-right"
													: "text-left")
											}
										>
											{h}
										</th>
									),
								)}
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-8 text-center text-sm text-muted"
									>
										<div className="flex items-center justify-center gap-2">
											<Loader2 className="h-4 w-4 animate-spin" />
											Loading stages…
										</div>
									</td>
								</tr>
							) : stages.length === 0 ? (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-8 text-center text-sm text-muted"
									>
										No custom stages yet.
									</td>
								</tr>
							) : (
								stages.map((s, i) => (
									<CustomStageRow
										key={s.id}
										stage={s}
										index={i}
										projects={projects}
										canManage={canManage}
										onEdit={openEdit}
										onDelete={setDeleteTarget}
										onChanged={mutate}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
			</SectionBlock>

			<StageDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				editing={editing}
				existing={stages}
				onSaved={mutate}
			/>

			<DeleteDialog
				stage={deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onDeleted={mutate}
			/>

			<PlacementDialog
				open={placementOpen}
				onClose={() => setPlacementOpen(false)}
				onPlacementChanged={mutate}
			/>
		</>
	);
}
